import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();

  const signIn = async () => {
    try {
      // Ask backend for a Google authorization URL (includes state + PKCE)
      // We tell the backend which callback URL to use — must match Google's
      // configured Authorized redirect URIs for this OAuth client.
      const redirect_uri = `${window.location.origin}/auth/google/callback`;
      const { data } = await api.get("/auth/google/start", { params: { redirect_uri } });
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google sign-in");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FCFCFC]" data-testid="login-page">
      {/* Visual side */}
      <div className="relative bg-neutral-900 text-white hidden lg:flex flex-col justify-between p-10 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div>
          <div className="label-eyebrow text-white/60">QUANTZILLION</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-1">IoT // CONTROL</div>
        </div>
        <div className="relative z-10">
          <div className="label-eyebrow text-white/60">// IDENTITY</div>
          <h2 className="font-display font-black tracking-tighter text-4xl mt-3 leading-tight">
            Sign in to your<br />operations console.
          </h2>
          <p className="text-white/70 mt-4 max-w-md text-sm leading-relaxed">
            Authentication is handled by Google.
            Your account is created automatically the first time you sign in.
          </p>
        </div>
        <div className="font-mono text-xs text-white/50">// session_token expires in 7 days</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
            className="label-eyebrow text-neutral-500 hover:text-neutral-900 mb-10"
          >← Back home</button>

          <h1 className="font-display font-black tracking-tighter text-4xl">Welcome.</h1>
          <p className="text-neutral-600 mt-3">Sign in with Google to access your IoT dashboard.</p>

          <button
            onClick={signIn}
            data-testid="google-signin-btn"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 px-5 py-4 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white transition-colors font-semibold"
          >
            <GoogleIcon /> Continue with Google
            <ArrowUpRight className="w-4 h-4 ml-auto" />
          </button>

          <div className="mt-8 text-xs text-neutral-500 font-mono leading-relaxed">
            By signing in you agree to a multi-tenant data model:
            each Google account is one isolated tenant. JWT email/password
            auth can be added later.
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.4 29.4 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.4 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c11 0 19.5-8 19.5-19.5 0-1.2-.1-2.3-.3-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.7 29.1 4.8 24 4.8 16.3 4.8 9.7 9 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 43.2c5 0 9.6-1.9 13.1-5l-6-5c-2 1.4-4.5 2.2-7.1 2.2-5.3 0-9.8-3.1-11.3-7.6l-6.6 5.1C9.5 38.9 16.2 43.2 24 43.2z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.6-3.7 4.7l6 5c-.4.4 6.4-4.7 6.4-13.7 0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}
